/**
 * A test plan's run matrix: the cases it owns, in the order they are declared,
 * with the conditions and input each one runs under (issue #105).
 *
 * READ-ONLY. "Add case" makes a def in the plan's package plus a usage of it
 * here (issue #113); including an existing case is the Includes section beside
 * this. Reordering members has no write path yet.
 *
 * Rows are the plan's members as the listing reports them: a case declared
 * inside the plan, or a usage of a case def. A usage states nothing of its own,
 * so its conditions and input are the ones it inherits from the def it is typed
 * by - the loader resolves that, and the row arrives with them filled in.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CreateVerificationDialog from '../entity/CreateVerificationDialog'
import { PAGE_LIMIT, api } from '../../api'
import { useCanWrite, useSchema } from '../../state/AppData'
import { findKind } from '../../schema'
import type { VerificationRow } from '../../schema'
import { MONOSPACE } from '../../theme'

/** The members of one plan, in the order the listing returns them. */
export function casesOf(rows: VerificationRow[], plan: { id: string; qualified_name: string }) {
  return rows.filter(
    (row) =>
      row.owner !== null &&
      row.owner !== undefined &&
      (row.owner === plan.id || row.owner === plan.qualified_name) &&
      row.verification_kind === 'case',
  )
}

/** The text of a cell, with a dash where nothing is stated. */
function stated(value: string | undefined) {
  return value ? (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {value}
    </Typography>
  ) : (
    <Typography variant="body2" color="text.disabled">
      —
    </Typography>
  )
}

export default function RunMatrix({
  plan,
}: {
  plan: { id: string; name: string; qualified_name: string }
}) {
  const schema = useSchema()
  const { allowed, reason } = useCanWrite()
  const kind = findKind(schema, 'verification')

  const [rows, setRows] = useState<VerificationRow[] | null>(null)
  const [failure, setFailure] = useState('')
  const [adding, setAdding] = useState(false)

  const load = useCallback(() => {
    api
      .verification({ limit: PAGE_LIMIT })
      .then((page) => {
        setFailure('')
        setRows(page.items)
      })
      .catch((error: Error) => setFailure(error.message))
  }, [])

  useEffect(() => {
    load()
  }, [load, plan.id])

  const cases = rows ? casesOf(rows, plan) : []

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
          Run matrix
        </Typography>
        {kind && (
          <Tooltip title={allowed ? '' : reason} describeChild>
            <span>
              <Button startIcon={<AddIcon />} onClick={() => setAdding(true)} disabled={!allowed}>
                Add case
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {failure && <Alert severity="error">{failure}</Alert>}

      {rows && cases.length === 0 && !failure && (
        <Typography variant="body2" color="text.secondary">
          This plan owns no cases yet.
        </Typography>
      )}

      {cases.length > 0 && (
        <Table size="small" aria-label="Run matrix">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Case</TableCell>
              <TableCell>Verifies</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Test conditions</TableCell>
              <TableCell>Input</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cases.map((row, index) => (
              <TableRow key={String(row.qualified_name)}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  {row.id ? (
                    <RouterLink to={`/verification/${encodeURIComponent(String(row.id))}`}>
                      <span style={{ fontFamily: MONOSPACE }}>{row.id}</span>
                    </RouterLink>
                  ) : (
                    // A usage has no short name; it reads as what it is typed by.
                    <span>{row.name || String(row.type_name ?? '')}</span>
                  )}
                  {row.name && row.id && row.name !== row.id ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {row.name}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ fontFamily: MONOSPACE }}>{row.verifies ?? '—'}</TableCell>
                <TableCell>{String(row.method ?? '') || '—'}</TableCell>
                <TableCell>{stated(row.test_conditions)}</TableCell>
                <TableCell>{stated(row.test_input)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        In declaration order; reordering is not available yet. Add case makes a new case in
        this plan’s package and includes it here; Includes below adds one that already exists.
      </Typography>

      {adding && kind && (
        <CreateVerificationDialog
          kind={kind}
          group="case"
          parent={{ qualifiedName: plan.qualified_name, label: plan.name || plan.id }}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false)
            load()
          }}
        />
      )}
    </Box>
  )
}
